import type { PvcConfiguration } from "../../../packages/pvc-product/pricing-controller";
import {
  createShopApiGraphqlClient,
  ShopApiRequestError,
  type ShopApiGraphqlClient,
} from "../../../packages/pvc-product/shop-api-transport";

export type GenericStorefrontCartConfiguration = {
  productSlug: string | null;
  widthCm: number | null;
  heightCm: number | null;
  addonCodes: string[];
  requestedDate: string | null;
  notes: string | null;
};

export type StorefrontCartLine = {
  id: string;
  orderLineVersion: number;
  quantity: number;
  productName: string;
  productVariantId: string;
  productSlug: string | null;
  sku: string;
  hasArtwork: boolean;
  kind: "PVC" | "CATALOG";
  linePriceWithTax: number;
  surchargeWithTax: number;
  totalWithTax: number;
  configuration: PvcConfiguration | null;
  genericConfiguration: GenericStorefrontCartConfiguration | null;
};

export type PvcStorefrontCartLine = StorefrontCartLine & {
  kind: "PVC";
  configuration: PvcConfiguration;
};

export function isPvcStorefrontCartLine(
  line: StorefrontCartLine,
): line is PvcStorefrontCartLine {
  return line.kind === "PVC" && line.configuration !== null;
}

export type StorefrontCart = {
  id: string;
  totalQuantity: number;
  totalWithTax: number;
  currencyCode: string;
  lines: StorefrontCartLine[];
};

export type StorefrontCartSummary = {
  totalQuantity: number;
};

const CART_SUMMARY_QUERY = `
  query HappyPrintCartSummary {
    pvcCart {
      totalQuantity
    }
  }
`;

const CART_QUERY = `
  query HappyPrintPvcCart {
    pvcCart {
      id
      totalQuantity
      totalWithTax
      currencyCode
      lines {
        id
        orderLineVersion
        quantity
        productName
        productVariantId
        productSlug
        sku
        hasArtwork
        kind
        linePriceWithTax
        surchargeWithTax
        totalWithTax
        configuration {
          widthCm
          heightCm
          quantity
          materialId
          addonIds
          requestedDate
        }
        genericConfiguration {
          productSlug
          widthCm
          heightCm
          addonCodes
          requestedDate
          notes
        }
      }
    }
  }
`;

const REMOVE_LINE_MUTATION = `
  mutation HappyPrintRemovePvcOrderLine($orderLineId: ID!) {
    removePvcOrderLine(orderLineId: $orderLineId) { id }
  }
`;

const REMOVE_CATALOG_LINE_MUTATION = `
  mutation HappyPrintRemoveCatalogOrderLine($orderLineId: ID!) {
    removeHappyPrintOrderLine(orderLineId: $orderLineId) { id }
  }
`;

function object(value: unknown, path: string): Record<string, unknown> {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

function string(value: unknown, path: string): string {
  if (typeof value !== "string" || value.length === 0) throw new Error(`${path} must be a non-empty string`);
  return value;
}

function number(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be a finite number`);
  return value;
}

function integer(value: unknown, path: string): number {
  const result = number(value, path);
  if (!Number.isInteger(result)) throw new Error(`${path} must be an integer`);
  return result;
}

function cartLineKind(value: unknown, path: string): StorefrontCartLine["kind"] {
  if (value === "PVC" || value === "CATALOG") return value;
  throw new Error(`${path} must be PVC or CATALOG`);
}

function nullableNumber(value: unknown, path: string): number | null {
  if (value == null) return null;
  return number(value, path);
}

function nullableString(value: unknown, path: string): string | null {
  if (value == null) return null;
  if (typeof value !== "string") throw new Error(`${path} must be a string or null`);
  return value;
}

function decodeGenericConfiguration(value: unknown, path: string): GenericStorefrontCartConfiguration {
  const item = object(value, path);
  if (!Array.isArray(item.addonCodes) || !item.addonCodes.every(code => typeof code === "string")) {
    throw new Error(`${path}.addonCodes must be a string array`);
  }
  return {
    productSlug: nullableString(item.productSlug, `${path}.productSlug`),
    widthCm: nullableNumber(item.widthCm, `${path}.widthCm`),
    heightCm: nullableNumber(item.heightCm, `${path}.heightCm`),
    addonCodes: [...item.addonCodes] as string[],
    requestedDate: nullableString(item.requestedDate, `${path}.requestedDate`),
    notes: nullableString(item.notes, `${path}.notes`),
  };
}

function decodeConfiguration(value: unknown, path: string): PvcConfiguration {
  const item = object(value, path);
  if (!Array.isArray(item.addonIds) || !item.addonIds.every(id => typeof id === "string")) {
    throw new Error(`${path}.addonIds must be a string array`);
  }
  const requestedDate = item.requestedDate;
  if (requestedDate != null && typeof requestedDate !== "string") throw new Error(`${path}.requestedDate must be a string or null`);
  return {
    widthCm: number(item.widthCm, `${path}.widthCm`),
    heightCm: number(item.heightCm, `${path}.heightCm`),
    quantity: integer(item.quantity, `${path}.quantity`),
    materialId: string(item.materialId, `${path}.materialId`),
    addonIds: [...item.addonIds] as string[],
    requestedDate: requestedDate as string | null,
  };
}

function decodeCartSummary(data: unknown): StorefrontCartSummary | null {
  const root = object(data, "data");
  if (root.pvcCart == null) return null;
  const cart = object(root.pvcCart, "data.pvcCart");
  return {
    totalQuantity: integer(cart.totalQuantity, "data.pvcCart.totalQuantity"),
  };
}

function isMissingActiveSession(error: unknown): boolean {
  return error instanceof ShopApiRequestError
    && error.code === "GRAPHQL"
    && error.message === "error.no-active-session";
}

function decodeCart(data: unknown): StorefrontCart | null {
  const root = object(data, "data");
  if (root.pvcCart == null) return null;
  const cart = object(root.pvcCart, "data.pvcCart");
  if (!Array.isArray(cart.lines)) throw new Error("data.pvcCart.lines must be an array");
  return {
    id: string(cart.id, "data.pvcCart.id"),
    totalQuantity: integer(cart.totalQuantity, "data.pvcCart.totalQuantity"),
    totalWithTax: integer(cart.totalWithTax, "data.pvcCart.totalWithTax"),
    currencyCode: string(cart.currencyCode, "data.pvcCart.currencyCode"),
    lines: cart.lines.map((value, index) => {
      const line = object(value, `data.pvcCart.lines[${index}]`);
      return {
        id: string(line.id, `data.pvcCart.lines[${index}].id`),
        orderLineVersion: integer(line.orderLineVersion, `data.pvcCart.lines[${index}].orderLineVersion`),
        quantity: integer(line.quantity, `data.pvcCart.lines[${index}].quantity`),
        productName: string(line.productName, `data.pvcCart.lines[${index}].productName`),
        productVariantId: string(line.productVariantId, `data.pvcCart.lines[${index}].productVariantId`),
        productSlug: nullableString(line.productSlug, `data.pvcCart.lines[${index}].productSlug`),
        sku: typeof line.sku === "string" ? line.sku : "",
        hasArtwork: line.hasArtwork === true,
        kind: cartLineKind(line.kind, `data.pvcCart.lines[${index}].kind`),
        linePriceWithTax: integer(line.linePriceWithTax, `data.pvcCart.lines[${index}].linePriceWithTax`),
        surchargeWithTax: integer(line.surchargeWithTax, `data.pvcCart.lines[${index}].surchargeWithTax`),
        totalWithTax: integer(line.totalWithTax, `data.pvcCart.lines[${index}].totalWithTax`),
        configuration: line.configuration == null
          ? null
          : decodeConfiguration(line.configuration, `data.pvcCart.lines[${index}].configuration`),
        genericConfiguration: line.genericConfiguration == null
          ? null
          : decodeGenericConfiguration(
              line.genericConfiguration,
              `data.pvcCart.lines[${index}].genericConfiguration`,
            ),
      };
    }),
  };
}

/** Every browser entry point (App routes, the homepage header) shares this
 * one factory so the Shop API endpoint lookup lives in exactly one place. */
export function createBrowserCartApi() {
  const endpoint = import.meta.env.VITE_SHOP_API_URL || "/shop-api";
  return createPvcCartApi(createShopApiGraphqlClient({ endpoint }));
}

export function createPvcCartApi(client: ShopApiGraphqlClient) {
  return {
    async loadSummary(): Promise<StorefrontCartSummary | null> {
      try {
        const data = await client.execute({
          operationName: "HappyPrintCartSummary",
          document: CART_SUMMARY_QUERY,
        });
        return decodeCartSummary(data);
      } catch (error) {
        if (isMissingActiveSession(error)) return null;
        throw error;
      }
    },

    async load(): Promise<StorefrontCart | null> {
      try {
        const data = await client.execute({
          operationName: "HappyPrintPvcCart",
          document: CART_QUERY,
        });
        return decodeCart(data);
      } catch (error) {
        // A first-time visitor legitimately has no Vendure session/order yet.
        // Treat only that explicit domain response as an empty cart; network,
        // HTTP and all other GraphQL failures must remain visible to the UI.
        if (isMissingActiveSession(error)) {
          return null;
        }
        throw error;
      }
    },

    async remove(orderLineId: string, kind: StorefrontCartLine["kind"] = "PVC"): Promise<void> {
      const data = await client.execute({
        operationName: kind === "PVC" ? "HappyPrintRemovePvcOrderLine" : "HappyPrintRemoveCatalogOrderLine",
        document: kind === "PVC" ? REMOVE_LINE_MUTATION : REMOVE_CATALOG_LINE_MUTATION,
        variables: { orderLineId },
      });
      if (kind === "CATALOG") {
        const result = object(
          object(data, "data").removeHappyPrintOrderLine,
          "data.removeHappyPrintOrderLine",
        );
        string(result.id, "data.removeHappyPrintOrderLine.id");
      }
    },
  };
}
